import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { WebinarData, WebinarsListState, WebinarState } from '../../types/state/webinar';
import { WebinarRegistrationStatus } from '../../constants/webinar';

const initialState: WebinarsListState = {
  webinars: {},
  sortedWebinarIds: [],
  isLoading: false,
  error: null,
};

const webinarSlice = createSlice({
  name: 'webinar',
  initialState,
  reducers: {
    setWebinars: (state, action: PayloadAction<WebinarData[]>) => {
      // Convert array to normalized object structure
      const webinars: Record<string, WebinarState> = {};
      const sortedWebinarIds: string[] = [];

      // Create a copy of the array before sorting to avoid mutating the read-only payload
      [...action.payload]
        .sort((a, b) => a.webinarScheduledTimestamp - b.webinarScheduledTimestamp)
        .forEach((webinar) => {
          webinars[webinar.webinarId] = {
            data: webinar,
            isLoading: false,
            error: null,
          };
          sortedWebinarIds.push(webinar.webinarId);
        });

      state.webinars = webinars;
      state.sortedWebinarIds = sortedWebinarIds;
    },
    updateWebinarRegistrationStatus: (state, action: PayloadAction<{
      webinarId: string;
      registrationStatus: WebinarRegistrationStatus;
      registrantURL?: string
    }>) => {
      const { webinarId, registrationStatus, registrantURL } = action.payload;
      if (state.webinars[webinarId]) {
        state.webinars[webinarId].data.registrationStatus = registrationStatus;
        if (registrantURL) {
          state.webinars[webinarId].data.registrantURL = registrantURL;
        }
      }
    },
  },
});

export const {
  setWebinars,
  updateWebinarRegistrationStatus,
} = webinarSlice.actions;

export { webinarSlice };
export default webinarSlice.reducer;

// Base selectors
export const selectSortedWebinarIds = (state: { webinar: WebinarsListState }) => state.webinar.sortedWebinarIds;
export const selectWebinarsMap = (state: { webinar: WebinarsListState }) => state.webinar.webinars;

// Memoized selectors
export const selectWebinarById = (
  state: { webinar: WebinarsListState },
  webinarId: string,
) => state.webinar.webinars[webinarId]?.data;

export const selectSortedWebinars = createSelector(
  [selectSortedWebinarIds, selectWebinarsMap],
  (sortedIds, webinarsMap) => sortedIds
    .map((id) => webinarsMap[id]?.data)
    .filter(Boolean) as WebinarData[],
);
